import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_RELAY_SETS, readJSON, writeJSON, StorageKey, type FeedInfo, type RelaySet } from '@/lib/jumble';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface FeedContextValue {
  relaySets: RelaySet[];
  addRelaySet: (set: RelaySet) => void;
  removeRelaySet: (id: string) => void;
  renameRelaySet: (id: string, name: string) => void;
  setRelaySetUrls: (id: string, urls: string[]) => void;
  feed: FeedInfo;
  setFeed: (feed: FeedInfo) => void;
  /** Resolved relay URLs for the active feed. */
  feedRelays: string[];
}

const FeedContext = createContext<FeedContextValue | undefined>(undefined);

export function useFeed() {
  const ctx = useContext(FeedContext);
  if (!ctx) throw new Error('useFeed must be used within FeedProvider');
  return ctx;
}

const DEFAULT_FEED: FeedInfo = { feedType: 'relays', id: 'global', name: 'Global' };

export function FeedProvider({ children }: { children: React.ReactNode }) {
  const { user } = useCurrentUser();
  const [relaySets, setRelaySets] = useState<RelaySet[]>(() => {
    const custom = readJSON<RelaySet[]>(StorageKey.RELAY_SETS, []);
    const merged = [...DEFAULT_RELAY_SETS];
    for (const r of custom) {
      if (!merged.some((d) => d.id === r.id)) merged.push(r);
    }
    return merged;
  });
  const [feed, setFeedState] = useState<FeedInfo>(() => readJSON<FeedInfo>(StorageKey.ACTIVE_FEED, DEFAULT_FEED));

  useEffect(() => {
    writeJSON(StorageKey.RELAY_SETS, relaySets.filter((r) => !DEFAULT_RELAY_SETS.some((d) => d.id === r.id)));
  }, [relaySets]);

  const setFeed = useCallback((f: FeedInfo) => {
    setFeedState(f);
    writeJSON(StorageKey.ACTIVE_FEED, f);
  }, []);

  const addRelaySet = useCallback((set: RelaySet) => {
    setRelaySets((prev) => (prev.some((r) => r.id === set.id) ? prev : [...prev, set]));
  }, []);
  const removeRelaySet = useCallback((id: string) => {
    if (DEFAULT_RELAY_SETS.some((d) => d.id === id)) return;
    setRelaySets((prev) => prev.filter((r) => r.id !== id));
  }, []);
  const renameRelaySet = useCallback((id: string, name: string) => {
    setRelaySets((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
  }, []);
  const setRelaySetUrls = useCallback((id: string, urls: string[]) => {
    setRelaySets((prev) => prev.map((r) => (r.id === id ? { ...r, relayUrls: urls } : r)));
  }, []);

  // Validate active feed against relay sets
  const feedRelays = useMemo(() => {
    if (feed.feedType === 'relay' && feed.id) return [feed.id];
    if (feed.feedType === 'relays' && feed.id) {
      const set = relaySets.find((r) => r.id === feed.id);
      if (set) return set.relayUrls;
    }
    if (feed.feedType === 'following' && !user) return DEFAULT_RELAY_SETS[0].relayUrls;
    return [];
  }, [feed, relaySets, user]);

  const value = useMemo(
    () => ({ relaySets, addRelaySet, removeRelaySet, renameRelaySet, setRelaySetUrls, feed, setFeed, feedRelays }),
    [relaySets, addRelaySet, removeRelaySet, renameRelaySet, setRelaySetUrls, feed, setFeed, feedRelays],
  );
  return <FeedContext.Provider value={value}>{children}</FeedContext.Provider>;
}
